import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, ArrowLeft, ShoppingBag, Truck, RefreshCw, Check, Heart } from "lucide-react";
import api, { formatCAD, resolveImg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { ProductCard } from "@/components/ProductCard";
import { Stars } from "@/components/Stars";
import { Reviews } from "@/components/Reviews";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setQty(1);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        api.get("/products", { params: { category: r.data.category } }).then((rr) =>
          setRelated(rr.data.filter((p) => p.id !== id).slice(0, 4))
        );
      })
      .catch(() => setNotFound(true));
    // live stock refresh to reflect in-store sales
    const t = setInterval(() => {
      api.get(`/products/${id}`).then((r) => setProduct((prev) => (prev ? { ...prev, stock: r.data.stock } : r.data))).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [id]);

  if (notFound)
    return (
      <div className="mx-auto max-w-3xl px-5 py-32 text-center">
        <p className="font-display text-4xl text-forest">Product not found</p>
        <Link to="/shop" className="mt-6 inline-flex items-center gap-2 text-clay hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to shop
        </Link>
      </div>
    );

  if (!product)
    return (
      <div className="mx-auto max-w-7xl px-5 py-12 lg:px-10">
        <div className="grid gap-10 lg:grid-cols-2">
          <div className="aspect-square animate-pulse rounded-3xl bg-muted" />
          <div className="space-y-4">
            <div className="h-8 w-2/3 animate-pulse rounded bg-muted" />
            <div className="h-24 animate-pulse rounded bg-muted" />
          </div>
        </div>
      </div>
    );

  const out = (product.stock ?? 0) <= 0;
  const max = product.stock ?? 0;

  return (
    <div data-testid="product-detail-page" className="mx-auto max-w-[1440px] px-5 py-8 lg:px-10 lg:py-10">
      <button onClick={() => navigate(-1)} className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold text-forest transition-colors hover:border-forest">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
          <div className="relative rounded-[2.25rem] border border-border/70 bg-white p-3 shadow-[0_30px_60px_-30px_rgba(15,74,35,0.3)] lg:sticky lg:top-36">
            <div className="overflow-hidden rounded-[1.75rem] bg-[#F6F1E6]">
              <img src={resolveImg(product.image_url)} alt={product.name} className="aspect-square w-full object-cover mix-blend-multiply" />
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
          <span className="label-eyebrow inline-flex rounded-full bg-leaf/10 px-3 py-1 text-forest">{product.category}</span>
          <h1 className="mt-4 text-balance font-display text-3xl font-semibold leading-[1.08] tracking-tight text-forest sm:text-4xl lg:text-5xl">
            {product.name}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">{product.unit}</p>
          {product.rating_count > 0 && (
            <a href="#reviews-section" className="mt-2 inline-flex items-center gap-2">
              <Stars value={product.rating_avg} />
              <span className="text-sm text-muted-foreground">{product.rating_avg?.toFixed(1)} · {product.rating_count} review{product.rating_count !== 1 ? "s" : ""}</span>
            </a>
          )}

          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-display text-4xl font-semibold text-clay" data-testid="detail-price">{formatCAD(product.price_cents)}</span>
            <span className="text-sm text-muted-foreground">CAD</span>
            <button
              data-testid="detail-wishlist-toggle"
              onClick={() => toggle(product)}
              aria-pressed={has(product.id)}
              className={`ml-auto inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${has(product.id) ? "border-forest bg-leaf/10 ring-1 ring-forest text-clay" : "border-border text-forest hover:border-clay/50"}`}
            >
              <Heart className={`h-4 w-4 ${has(product.id) ? "fill-clay text-clay" : ""}`} />
              {has(product.id) ? "Saved" : "Save"}
            </button>
          </div>

          <p className="mt-6 max-w-md leading-relaxed text-muted-foreground">{product.description}</p>

          {/* Stock indicator (live) */}
          <div data-testid="stock-indicator" className="mt-6 inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm">
            {out ? (
              <span className="font-semibold text-destructive">Out of stock</span>
            ) : max <= 5 ? (
              <>
                <span className="h-2 w-2 rounded-full bg-clay" />
                <span data-testid="low-stock-badge" className="font-semibold text-clay">Only {max} left</span>
                <RefreshCw className="ml-1 h-3 w-3 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">live inventory</span>
              </>
            ) : (
              <>
                <span className="h-2 w-2 animate-pulse rounded-full bg-leaf" />
                <span className="font-medium text-forest">{max} in stock</span>
                <RefreshCw className="ml-1 h-3 w-3 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">live inventory</span>
              </>
            )}
          </div>

          {/* Qty + add */}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <div className="flex items-center rounded-full border border-border bg-white">
              <button
                data-testid="qty-decrease"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                disabled={out}
                className="flex h-12 w-12 items-center justify-center rounded-full text-forest transition-colors hover:bg-forest/5 disabled:opacity-40"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span data-testid="qty-value" className="w-10 text-center font-semibold text-forest">{qty}</span>
              <button
                data-testid="qty-increase"
                onClick={() => setQty((q) => Math.min(max, q + 1))}
                disabled={out || qty >= max}
                className="flex h-12 w-12 items-center justify-center rounded-full text-forest transition-colors hover:bg-forest/5 disabled:opacity-40"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>

            <button
              data-testid="detail-add-to-cart"
              disabled={out}
              onClick={() => addItem(product, qty)}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-clay px-10 py-4 text-sm font-bold text-white shadow-[0_14px_30px_-12px_rgba(200,32,43,0.7)] transition-all duration-200 hover:bg-[#A3161F] active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground sm:flex-none"
            >
              <ShoppingBag className="h-4 w-4" /> {out ? "Sold out" : "Add to cart"}
            </button>
          </div>

          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            <p className="flex items-start gap-3 rounded-2xl bg-white p-4 text-sm text-muted-foreground ring-1 ring-border/70">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-leaf/10 text-forest"><Truck className="h-4 w-4" /></span>
              Canada-wide delivery with live shipping rates at checkout.
            </p>
            <p className="flex items-start gap-3 rounded-2xl bg-white p-4 text-sm text-muted-foreground ring-1 ring-border/70">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-leaf/10 text-forest"><Check className="h-4 w-4" /></span>
              Same inventory as our physical store — always accurate.
            </p>
          </div>
        </motion.div>
      </div>

      <div id="reviews-section">
        <Reviews
          productId={product.id}
          ratingAvg={product.rating_avg || 0}
          ratingCount={product.rating_count || 0}
          onChange={(avg, count) => setProduct((p) => ({ ...p, rating_avg: avg, rating_count: count }))}
        />
      </div>

      {related.length > 0 && (
        <div className="mt-24">
          <h2 className="font-display text-4xl font-semibold tracking-tight text-forest">You might also like</h2>
          <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-4">
            {related.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
